import {
	addMonths,
	addQuarters,
	addWeeks,
	addYears,
	endOfISOWeek,
	endOfMonth,
	endOfQuarter,
	endOfYear,
	format,
	startOfDay,
	startOfISOWeek,
	startOfMonth,
	startOfQuarter,
	startOfYear,
} from 'date-fns';

/******************************************************************
 * 파일명	: frontend/src/helpers/dateRange.js
 * 설명		: 관리자 화면 "기간 선택 UI"(CalendarBox / CalendarPresetFilter)에서
 *          쓰는 날짜 범위 계산 함수 모음.
 ******************************************************************/

// 달력 아이콘에서 선택한 기간(주/월/분기/반기/년) 단위로 이전·다음 구간을 계산
export const shiftPeriodRange = (baseDate, periodType, direction) => {
	const sign = direction === 'prev' ? -1 : 1;

	switch (periodType) {
		case 'month': {
			const shifted = addMonths(baseDate, sign);
			return { startDate: startOfMonth(shifted), endDate: endOfMonth(shifted) };
		}
		case 'quarter': {
			const shifted = addQuarters(baseDate, sign);
			return { startDate: startOfQuarter(shifted), endDate: endOfQuarter(shifted) };
		}
		case 'half': {
			const shifted = addMonths(baseDate, sign * 6);
			const half = shifted.getMonth() < 6 ? 0 : 1; // 0: 상반기, 1: 하반기
			return {
				startDate: new Date(shifted.getFullYear(), half * 6, 1),
				endDate: new Date(shifted.getFullYear(), half * 6 + 6, 0),
			};
		}
		case 'year': {
			const shifted = addYears(baseDate, sign);
			return { startDate: startOfYear(shifted), endDate: endOfYear(shifted) };
		}
		case 'week':
		default: {
			const shifted = addWeeks(baseDate, sign);
			return { startDate: startOfISOWeek(shifted), endDate: endOfISOWeek(shifted) };
		}
	}
};

// 월별 탭 화살표: 현재 표시 중인 범위의 개월 수(분기 선택 시 3개월, 반기 선택 시 6개월 등)만큼 이동
export const shiftMonthTabRange = (currentRange, direction) => {
	const { startDate, endDate } = currentRange;
	const spanInMonths =
		(endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth()) + 1;
	const sign = direction === 'prev' ? -1 : 1;

	const newStartDate = addMonths(startOfMonth(startDate), sign * spanInMonths);
	const newEndDate = endOfMonth(addMonths(newStartDate, spanInMonths - 1));

	return { startDate: newStartDate, endDate: newEndDate };
};

// <, > 클릭 시 다음에 표시할 날짜 범위를 계산
//  - logState === 'month' : 현재 범위의 개월 수만큼 이동 (shiftMonthTabRange)
//  - 그 외                : 일별이면 달력 아이콘에서 고른 프리셋 단위, 주별/월별이면 해당 단위로 이동
export const getShiftedRange = ({ logState, selectedButton, currentRange, direction }) => {
	if (logState === 'month') {
		const { startDate, endDate } = shiftMonthTabRange(currentRange, direction);
		return [{ key: 'selection', startDate, endDate }];
	}

	const periodType = logState === 'day' ? selectedButton?.status?.value || 'week' : logState;
	const baseDate = direction === 'prev' ? currentRange.startDate : currentRange.endDate;
	const { startDate, endDate } = shiftPeriodRange(baseDate, periodType, direction);

	return [{ key: 'selection', startDate, endDate: startOfDay(endDate) }];
};

// 달력 아이콘 팝오버에서 프리셋(주/월/분기/반기/년)을 고르고 "조회"를 눌렀을 때의 날짜 범위 계산
//  - week            : 기준일(currentStartDate)이 속한 주(월요일 ~ 일요일)
//  - month/quarter/half : 올해 기준
//  - year            : 선택한 연도(filterText)
//  대상 프리셋이 아니면 null 반환
export const getPresetRange = (filterValue, filterText, currentStartDate) => {
	const today = new Date();

	if (filterValue === 'week') {
		const currentTemp = currentStartDate;
		const currentDayOfWeek = currentTemp.getDay();

		const startOfWeek = new Date(currentTemp);
		startOfWeek.setDate(currentTemp.getDate() - currentDayOfWeek + 1);

		const endOfWeek = new Date(currentTemp);
		endOfWeek.setDate(currentTemp.getDate() + (7 - currentDayOfWeek));

		return { startDate: startOfWeek, endDate: endOfWeek };
	}

	if (filterValue === 'month') {
		const filterMonth = parseInt(filterText, 10);
		return {
			startDate: new Date(today.getFullYear(), filterMonth - 1, 1),
			endDate: new Date(today.getFullYear(), filterMonth, 0),
		};
	}

	if (filterValue === 'quarter') {
		const filterQuarter = parseInt(filterText, 10);
		const startMonthOfQuarter = (filterQuarter - 1) * 3 + 1;
		const endMonthOfQuarter = filterQuarter * 3;
		return {
			startDate: new Date(today.getFullYear(), startMonthOfQuarter - 1, 1),
			endDate: new Date(today.getFullYear(), endMonthOfQuarter, 0),
		};
	}

	if (filterValue === 'half') {
		const filterTextToPar = parseInt(filterText, 10);
		const startMonthOfHalf = filterTextToPar === 1 ? 0 : 6;
		const endMonthOfHalf = filterTextToPar === 1 ? 5 : 11;
		return {
			startDate: new Date(today.getFullYear(), startMonthOfHalf, 1),
			endDate: new Date(today.getFullYear(), endMonthOfHalf + 1, 0),
		};
	}

	if (filterValue === 'year') {
		const filterYear = parseInt(filterText, 10);
		return {
			startDate: new Date(filterYear, 0, 1),
			endDate: new Date(filterYear, 11, 31),
		};
	}

	return null;
};

// 상단 날짜 박스에 표시할 텍스트
export const formatDateRangeLabel = (logState, selectionRange, locale) => {
	const { startDate, endDate } = selectionRange[0];
	const localeOpt = locale ? { locale } : {};

	if (logState === 'week') {
		return `${format(startOfISOWeek(startDate), 'yyyy.MM.dd')} ~ ${format(endOfISOWeek(endDate), 'yyyy.MM.dd')}`;
	}

	if (logState === 'month') {
		const startLabel = format(startDate, 'MMMM yyyy', localeOpt);
		const endLabel = format(endDate, 'MMMM yyyy', localeOpt);
		return startLabel === endLabel ? startLabel : `${startLabel} ~ ${endLabel}`;
	}

	return `${format(startDate, 'yyyy.MM.dd')} ~ ${format(endDate, 'yyyy.MM.dd')}`;
};
